import axios from 'axios';

/**
 * Global auth failure handler. If the server rejects our token as invalid or
 * expired (e.g. after a server-side secret rotation, or natural expiry), clear
 * the stale session and send the user to the login page — once.
 *
 * Registered on the default axios instance. App.js issues its first
 * authenticated call (`/subscription/check-status`) on load through this
 * instance, so a dead token is caught immediately on refresh.
 */
let handled = false;

const AUTH_HINT = /token|expired|credential|unauthor|jwt|forbidden/i;

const isAuthFailure = (error) => {
  const status = error?.response?.status;
  if (status !== 401 && status !== 403) return false;
  const body = error.response.data;
  const text = typeof body === 'string' ? body : JSON.stringify(body || {});
  // 403s that are about *permissions* (not a bad token) shouldn't log you out.
  if (status === 403 && !AUTH_HINT.test(text)) return false;
  return true;
};

const onRejected = (error) => {
  if (!handled && isAuthFailure(error) && localStorage.getItem('streamToken')) {
    handled = true;
    try {
      ['streamToken', 'streamUser', 'userDetails', 'token', 'user', 'hasSubscription']
        .forEach((k) => localStorage.removeItem(k));
    } catch (_) { /* ignore */ }
    const p = window.location.pathname;
    if (!p.startsWith('/login') && !p.startsWith('/register') && !p.startsWith('/page-login')) {
      window.location.assign('/login');
    }
  }
  return Promise.reject(error);
};

axios.interceptors.response.use((r) => r, onRejected);

// Services create their own axios instances (axios.create(...)); make sure each
// one inherits the auth-failure handler.
const origCreate = axios.create.bind(axios);
axios.create = (...args) => {
  const instance = origCreate(...args);
  instance.interceptors.response.use((r) => r, onRejected);
  return instance;
};

export default axios;
